import { create } from "zustand";
import { http } from "@/libs/http";

export interface PortalUser {
  id: string;
  name: string;
  email: string;
  status: number; // 0=pending, 1=active, 2=inactive, 3=locked
  avatar_url?: string | null;
}

export interface PortalApp {
  id: string;
  resource_id: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
  base_url?: string | null;
  is_enabled: boolean;
  platforms?: string[];
}

interface PortalState {
  user: PortalUser | null;
  apps: PortalApp[];
  isLoading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  reset: () => void;
}

export const usePortal = create<PortalState>((set) => ({
  user: null,
  apps: [],
  isLoading: false,
  error: null,

  fetch: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await http.get<{ user: PortalUser; apps: PortalApp[] }>(
        "/iam/v1/portal/apps"
      );
      set({ user: data.user, apps: data.apps, isLoading: false });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // Structured 403 for pending users — bubble up for the page to handle
      if (err?.response?.status === 403 && detail?.code === "user_pending") {
        set({ isLoading: false, error: "user_pending" });
        return;
      }
      set({
        isLoading: false,
        error: err?.response?.data?.detail || err?.message || "Failed to load portal",
      });
    }
  },

  reset: () => set({ user: null, apps: [], isLoading: false, error: null }),
}));
